class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    // Use "change" instead of "input" to prevent native select dropdown from
    // re-rendering immediately on open in some browsers.
    this.querySelector('form').addEventListener('change', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      // Cross-collection (subcollection) navigation stores the path it landed on.
      if (event.state && event.state.subnavPath) {
        FacetFiltersForm.renderCollectionPage(event.state.subnavPath, false);
        return;
      }
      // Back/forward to a record whose pathname differs from the collection we
      // currently have rendered (e.g. back to the original collection after a
      // subcollection AJAX jump). The original record has no subnavPath state.
      if (window.location.pathname !== FacetFiltersForm.currentPath) {
        FacetFiltersForm.renderCollectionPage(window.location.pathname + window.location.search, false);
        return;
      }
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  // Navigate to another collection (a subcollection tab) without a full reload:
  // fetch the section for the target collection, swap the grid, toolbar and filter
  // modal, and push the new collection URL into history.
  static renderCollectionPage(target, updateHistory = true) {
    const grid = document.getElementById('product-grid');
    const sectionId = grid ? grid.dataset.id : null;
    const targetUrl = new URL(target, window.location.origin);
    const displayPath = `${targetUrl.pathname}${targetUrl.search}`;

    if (!sectionId) {
      window.location.href = displayPath;
      return;
    }

    const gridContainer = document.getElementById('ProductGridContainer');
    const loadingTarget = gridContainer ? gridContainer.querySelector('.collection') : null;
    if (loadingTarget) loadingTarget.classList.add('loading');

    // The collection title lives in its own "collection-headline" section, so it
    // has to be fetched and swapped together with the grid section.
    const headlineWrapper = document.querySelector('.headline__container');
    const headlineSection = headlineWrapper ? headlineWrapper.closest('.shopify-section') : null;
    const headlineSectionId = headlineSection ? headlineSection.id.replace('shopify-section-', '') : null;

    const fetchUrl = new URL(targetUrl);
    const sectionIds = headlineSectionId ? `${sectionId},${headlineSectionId}` : sectionId;
    fetchUrl.searchParams.set('sections', sectionIds);

    fetch(fetchUrl)
      .then((response) => response.json())
      .then((sectionsHtml) => {
        const gridHtml = sectionsHtml[sectionId];
        if (typeof gridHtml !== 'string') {
          window.location.href = displayPath;
          return;
        }
        const parsed = new DOMParser().parseFromString(gridHtml, 'text/html');

        const newGrid = parsed.getElementById('ProductGridContainer');
        if (newGrid && gridContainer) gridContainer.innerHTML = newGrid.innerHTML;

        const headlineHtml = headlineSectionId ? sectionsHtml[headlineSectionId] : null;
        if (typeof headlineHtml === 'string' && headlineSection) {
          const parsedHeadline = new DOMParser().parseFromString(headlineHtml, 'text/html');
          const newHeadlineSection = parsedHeadline.getElementById(headlineSection.id);
          if (newHeadlineSection) headlineSection.innerHTML = newHeadlineSection.innerHTML;
        }

        const newToolbar = parsed.querySelector('.collection-toolbar');
        const curToolbar = document.querySelector('.collection-toolbar');
        if (newToolbar && curToolbar) curToolbar.innerHTML = newToolbar.innerHTML;

        const newFilters = parsed.querySelector('.filter-modal__container');
        const curFilters = document.querySelector('.filter-modal__container');
        if (newFilters && curFilters) curFilters.innerHTML = newFilters.innerHTML;

        // New collection = new filter context; drop the cached filter responses
        // and remember what is now on screen so popstate can detect a switch back.
        FacetFiltersForm.filterData = [];
        FacetFiltersForm.currentPath = targetUrl.pathname;
        FacetFiltersForm.searchParamsPrev = targetUrl.search.slice(1);

        if (updateHistory) history.pushState({ subnavPath: displayPath }, '', displayPath);

        if (typeof resizeAllMasonryItems === 'function') resizeAllMasonryItems();
      });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer){
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    if (typeof resizeAllMasonryItems === 'function') resizeAllMasonryItems();
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));

    const select = document.querySelector('.select__select');
    const customSelectLabel = document.querySelector('.select__div span');
    if (select && customSelectLabel) {
      customSelectLabel.textContent = Array.from(select.querySelectorAll('option')).find((radio) => radio.value === select.value).textContent;
    }

  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting', '.collection-toolbar'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    // document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  // The filter modal is not re-rendered by AJAX (it has no .js-filter blocks),
  // so clearing filters must reset its inputs manually: checkboxes (colors,
  // sizes, availability) and the price range fields. Hidden inputs (sort_by, q)
  // are left untouched.
  static resetFilterInputs() {
    document.querySelectorAll('#FacetFiltersForm input[type="checkbox"]').forEach((input) => {
      input.checked = false;
    });
    document.querySelectorAll('#FacetFiltersForm input[type="number"]').forEach((input) => {
      input.value = '';
    });
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.currentPath = window.location.pathname;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// Custom sort dropdown — event delegation works across re-renders
function closeSortDropdowns(except) {
  document.querySelectorAll('.collection-toolbar__sort-dropdown.is-open').forEach((d) => {
    if (d === except) return;
    d.classList.remove('is-open');
    const t = d.querySelector('.collection-toolbar__sort-trigger');
    if (t) t.setAttribute('aria-expanded', 'false');
  });
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('.collection-toolbar__sort-trigger');
  if (trigger) {
    const dropdown = trigger.closest('.collection-toolbar__sort-dropdown');
    const isOpen = dropdown.classList.contains('is-open');
    closeSortDropdowns(dropdown);
    dropdown.classList.toggle('is-open', !isOpen);
    trigger.setAttribute('aria-expanded', String(!isOpen));
    return;
  }

  const option = event.target.closest('.collection-toolbar__sort-option');
  if (option) {
    const dropdown = option.closest('.collection-toolbar__sort-dropdown');
    const triggerEl = dropdown.querySelector('.collection-toolbar__sort-trigger');
    const labelEl = dropdown.querySelector('.collection-toolbar__sort-label');
    const selectEl = dropdown.querySelector('select');

    labelEl.textContent = option.textContent.trim();
    selectEl.value = option.dataset.value;

    dropdown.querySelectorAll('.collection-toolbar__sort-option').forEach((o) => {
      o.classList.toggle('is-selected', o === option);
      o.setAttribute('aria-selected', String(o === option));
    });

    dropdown.classList.remove('is-open');
    triggerEl.setAttribute('aria-expanded', 'false');
    selectEl.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }

  if (!event.target.closest('.collection-toolbar__sort-dropdown')) {
    closeSortDropdowns();
  }
});

document.addEventListener('click', (event) => {
  const categoryLink = event.target.closest('.collection-toolbar__category-link');
  if (!categoryLink) return;

  const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
  if (isModifiedClick || event.button !== 0 || categoryLink.target === '_blank') return;

  const href = categoryLink.getAttribute('href');
  if (!href) return;

  const targetUrl = new URL(href, window.location.origin);
  if (targetUrl.origin !== window.location.origin || targetUrl.pathname !== window.location.pathname) return;

  event.preventDefault();
  const searchParams = targetUrl.search.replace(/^\?/, '');
  FacetFiltersForm.renderPage(searchParams, event);
});

// Toolbar "Filters [N]" chip clear (×): reset all facets via AJAX, like Apply,
// instead of a full page reload. Capture phase + stopPropagation prevents the
// surrounding modal-opener button from also opening the filter sidebar.
document.addEventListener('click', (event) => {
  const clearButton = event.target.closest('.collection-toolbar__clear-active');
  if (!clearButton) return;

  event.preventDefault();
  event.stopPropagation();
  FacetFiltersForm.resetFilterInputs();
  FacetFiltersForm.renderPage('');
}, true);

// Subcollection tabs: AJAX navigation to another collection page.
document.addEventListener('click', (event) => {
  const subnavLink = event.target.closest('.collection-subnav__link');
  if (!subnavLink) return;

  const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
  if (isModifiedClick || event.button !== 0 || subnavLink.target === '_blank') return;

  const href = subnavLink.getAttribute('href');
  if (!href) return;

  const targetUrl = new URL(href, window.location.origin);
  if (targetUrl.origin !== window.location.origin) return;

  event.preventDefault();
  if (targetUrl.pathname === window.location.pathname) return;
  FacetFiltersForm.renderCollectionPage(targetUrl.pathname);
});

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector('a').addEventListener('click', (event) => {
      event.preventDefault();
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      FacetFiltersForm.resetFilterInputs();
      form.onActiveFilterClick(event);
    });
  }
}

customElements.define('facet-remove', FacetRemove);
